import { FC } from 'react';
import { ReactNode } from '@tanstack/react-router';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import {
    BookablePeriod,
    GeneralPricingMode,
    ResourceCategoryType,
    ServiceReservationPricing,
    TimeUnitPeriod,
} from '@mews-commander/data-access';
import { LocalizedEnum, lt } from '@mews/framework';
import { Stack, Typography } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { ConvertedCurrencyValue, SpacedMultiplicationSign } from '@mews-commander/ui';
import { getBookablePeriodPluralTextKey } from '@mews-commander/utils';

export interface TotalSummaryProps {
    rateName?: string;
    prices: ServiceReservationPricing;
    resourceCategoryType: ResourceCategoryType | undefined;
    spacesCount: number;
    totalLabel?: ReactNode;
    timeUnitPeriod: TimeUnitPeriod;
    bookablePeriod: BookablePeriod;
    bookablePeriodsDiffs: Record<BookablePeriod, number>;
}

export const TotalSummary: FC<TotalSummaryProps> = ({
    rateName,
    prices,
    resourceCategoryType,
    spacesCount,
    totalLabel,
    bookablePeriodsDiffs,
    timeUnitPeriod,
    bookablePeriod,
}) => {
    const enterprise = useCurrentEnterprise();
    const net = enterprise.pricing === GeneralPricingMode.Net;

    // for monthly services we decided to show days instead of calendar months
    const period: BookablePeriod = timeUnitPeriod !== TimeUnitPeriod.Month ? bookablePeriod : BookablePeriod.Day;

    const count = bookablePeriodsDiffs[period];
    const timeUnitTextKey = count <= 1 ? period : getBookablePeriodPluralTextKey(period);

    return (
        <Stack vertical spacing="150" data-test-id="new-reservation-summary">
            <Typography textStyle="bodyLargeStrong" as="div">
                <Stack distribution="space-between">
                    {totalLabel ?? lt('Total')}
                    <ConvertedCurrencyValue amount={prices.total} net={net} />
                </Stack>
            </Typography>
            <Stack vertical spacing="50">
                <Stack distribution="space-between">
                    <span>
                        <ConvertedCurrencyValue amount={prices.averagePerNight} net={net} />
                        {SpacedMultiplicationSign}
                        {count}
                        {UnicodeEntity.NoBreakSpace}
                        {lt(timeUnitTextKey)}
                        {SpacedMultiplicationSign}
                        {spacesCount}
                        {UnicodeEntity.NoBreakSpace}
                        {resourceCategoryType && (
                            <LocalizedEnum prefix="ResourceCategoryType" value={resourceCategoryType} />
                        )}
                    </span>
                    <Typography textStyle="bodyMedium" variant="secondary" as="div">
                        {rateName}
                    </Typography>
                </Stack>
            </Stack>
        </Stack>
    );
};
